/**
 * Display-side money formatting.
 *
 * All arithmetic happens on the server in decimal; JavaScript numbers are only ever used to
 * render a value the server already computed. Nothing here should be used to derive a total
 * that gets sent back — binary floating point cannot be trusted with money.
 */

const usdFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const lbpFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
})

export function formatUsd(amount: number): string {
  return usdFormatter.format(amount)
}

/** LBP has no practical subunit, so it is always whole and grouped. */
export function formatLbp(amount: number): string {
  return `${lbpFormatter.format(Math.round(amount))} LL`
}

export function formatQuantity(quantity: number): string {
  // Weighed goods need decimals; a tin of tuna should not read "3.000".
  return Number.isInteger(quantity)
    ? quantity.toString()
    : quantity.toFixed(3).replace(/0+$/, '').replace(/\.$/, '')
}

export function formatPercent(fraction: number): string {
  return `${(fraction * 100).toFixed(fraction * 100 % 1 === 0 ? 0 : 1)}%`
}

/**
 * A moment, shown as the day and the time on it.
 *
 * Two entries against one customer on one day are told apart by the clock, so a statement that
 * shows only the date leaves the owner unable to say which payment is which.
 */
export function formatMoment(iso: string): string {
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return iso

  return `${at.toLocaleDateString()} ${at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
}
